import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { privacyPolicy, termsOfUse } from "../data/legal";
export type LegalTab = "privacy" | "terms";
interface LegalModalProps {
  tab: LegalTab | null;
  onChangeTab: (tab: LegalTab) => void;
  onClose: () => void;
}
const TABS: {
  id: LegalTab;
  label: string;
}[] = [{
  id: "privacy",
  label: "Políticas de Privacidad"
}, {
  id: "terms",
  label: "Términos de Uso"
}];
export function LegalModal({
  tab,
  onChangeTab,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (!tab) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [tab, onClose]);
  const sections = tab === "terms" ? termsOfUse : privacyPolicy;
  return <AnimatePresence data-id="element-71" data-mp="1aqydcmdy6m1x">
      {tab && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-cocoa/50 p-0 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} onClick={onClose} data-id="element-72" data-mp="1apn8ej8rn898">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-cream sm:rounded-3xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-73" data-mp="1pn79vg6gelan">
            <div className="flex items-center justify-between border-b border-blush-100 px-5 py-4" data-id="element-74" data-mp="97mr3cunp15d">
              <h2 id="legal-title" className="font-serif text-xl font-semibold text-cocoa" data-id="element-75" data-mp="e67icmrxkaho">
                Información legal
              </h2>
              <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full p-2 text-cocoa-soft transition-colors duration-150 hover:bg-blush-50 hover:text-cocoa focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400" data-id="element-76" data-mp="17q8zqycqala">
                <XIcon className="h-5 w-5" data-id="element-77" data-mp="b0xdl9l9pmkb" />
              </button>
            </div>
            <div className="flex gap-1 border-b border-blush-100 px-5 pt-3" role="tablist" data-id="element-78" data-mp="3jgh07prfo6qe">
              {TABS.map(t => <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => onChangeTab(t.id)} className={`whitespace-nowrap border-b-2 px-3 pb-2.5 text-sm font-medium transition-colors duration-150 ${tab === t.id ? "border-rose-600 text-cocoa" : "border-transparent text-cocoa-soft hover:text-cocoa"}`} data-id="element-79" data-mp="3ehnwgp1y7u3f">
                  {t.label}
                </button>)}
            </div>
            <div className="overflow-y-auto px-5 py-5" role="tabpanel" data-id="element-80" data-mp="13pkyv560obxv">
              <div className="space-y-4" data-id="element-81" data-mp="1b5g7fulic9hl">
                {sections.map(s => <div key={s.title} data-id="element-82" data-mp="1g648l93ga6pj">
                    <h3 className="text-sm font-semibold text-cocoa" data-id="element-83" data-mp="1g75kffsaix8b">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-cocoa-soft" data-id="element-84" data-mp="127e95mt5ruy0">{s.body}</p>
                  </div>)}
              </div>
              <p className="mt-6 text-xs text-cocoa-faint" data-id="element-85" data-mp="169hoimiy7jok">Última actualización: septiembre de 2026.</p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}